import { Trans, useLingui } from "@lingui/react/macro";
import type { ChatModelChoice, ModelChoiceSort } from "@rakazo/core";
import { groupModelChoices, modelChoiceProviders } from "@rakazo/core";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  NativeSelect,
  NativeSelectOption,
  ToggleGroup,
  ToggleGroupItem,
} from "@rakazo/ui-web";
import { useMemo, useState } from "react";
import { TextOnlyMark } from "./TextOnlyMark";

/**
 * A searchable model list. With models from more than one provider it can be narrowed to one
 * provider and ordered by provider or by model name, so one large provider does not bury the rest.
 */
export function ModelChoiceCommand({
  choices,
  value,
  onSelect,
  imagesHintId,
}: {
  choices: readonly ChatModelChoice[];
  value?: string;
  onSelect: (choice: ChatModelChoice) => void;
  /** Describes a model that cannot read images. */
  imagesHintId?: string;
}) {
  const { t } = useLingui();
  const [query, setQuery] = useState("");
  const [provider, setProvider] = useState("");
  const [sort, setSort] = useState<ModelChoiceSort>("provider");
  const providers = useMemo(() => modelChoiceProviders(choices), [choices]);
  const sections = useMemo(
    () => groupModelChoices(choices, query, { provider: provider || undefined, sort }),
    [choices, query, provider, sort],
  );

  return (
    <Command
      shouldFilter={false}
      className="h-auto min-h-0 max-h-(--available-height) rounded-none bg-popover *:data-[slot=command-input-wrapper]:shrink-0"
    >
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder={t`Search`}
        aria-label={t`Search models`}
      />
      {providers.length > 1 ? (
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-2 py-1.5">
          <NativeSelect
            size="sm"
            className="min-w-0 flex-1"
            aria-label={t`Provider`}
            value={provider}
            onChange={(event) => setProvider(event.target.value)}
          >
            <NativeSelectOption value="">{t`All providers`}</NativeSelectOption>
            {providers.map((name) => (
              <NativeSelectOption key={name} value={name}>
                {name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <ToggleGroup
            aria-label={t`Sort by`}
            value={[sort]}
            onValueChange={([next]) => {
              if (next === "provider" || next === "model") setSort(next);
            }}
            spacing={1}
            className="shrink-0 rounded-lg bg-muted p-0.5"
          >
            <ToggleGroupItem
              value="provider"
              size="sm"
              className="px-2 text-[13px] aria-pressed:bg-background aria-pressed:shadow-sm"
            >
              <Trans>Provider</Trans>
            </ToggleGroupItem>
            <ToggleGroupItem
              value="model"
              size="sm"
              className="px-2 text-[13px] aria-pressed:bg-background aria-pressed:shadow-sm"
            >
              <Trans>Model</Trans>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      ) : null}
      <CommandList className="min-h-0 max-h-80 flex-1 overflow-y-auto">
        <CommandEmpty>
          <Trans>No matching models</Trans>
        </CommandEmpty>
        {sections.map((section) => (
          <CommandGroup key={section.key} heading={section.heading}>
            {section.data.map((choice) => (
              <CommandItem
                key={choice.key}
                aria-label={choice.label}
                // cmdk falls back to the row's text for an empty value.
                value={choice.key || "space-default"}
                data-checked={value !== undefined && choice.key === value}
                aria-describedby={choice.images === false ? imagesHintId : undefined}
                onSelect={() => onSelect(choice)}
              >
                <span className="min-w-0 flex-1 truncate">{choice.title ?? choice.label}</span>
                {sort === "model" && choice.providerLabel ? (
                  <span className="shrink-0 truncate text-[13px] text-muted-foreground">
                    {choice.providerLabel}
                  </span>
                ) : null}
                {choice.images === false ? (
                  <span className="shrink-0 text-muted-foreground">
                    <TextOnlyMark />
                  </span>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </Command>
  );
}
